import React, { useState, useEffect, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import Strapi from '../../services/strapi';
import Keywords from '../../atoms/Keywords';
import TinyMCE from '../../atoms/TinyMCE';
import ArticleEditorProps from '../../interfaces/ArticleEditorProps';
import ArticleField from '../../atoms/ArticleField';
import { article_to_readintern, readintern_to_article, ArticlePicker } from '../../atoms/ReadIntern';
import Title from '../../atoms/Title';
import Surtitle from '../../atoms/SurTitle';
import MetaTitle from '../../atoms/MetaTitle';
import UrlTitle from '../../atoms/UrlTitle';
import ShortHat from '../../atoms/ShortHat';
import LongHat from '../../atoms/LongHat';
import SeeInMostView from '../../atoms/SeeInMostView';
import MainTheme from '../../atoms/MainTheme';
import SecondaryThemes from '../../atoms/SecondaryThemes';
import Image from '../../atoms/Image';
import Legend from '../../atoms/Legend';
import SendToPartnerFeed from '../../atoms/SendToPartnerFeed';
import RevisionMessage from '../../atoms/RevisionMessage';
import ArticleSnackbar from '../../atoms/ArticleSnackbar'
import { useParams } from 'react-router-dom';
import { Share, Notif } from '../../interfaces/article';
import SharePicker from '../../atoms/SharePicker';
import NotificationPicker from '../../atoms/NotificationPicker';

const DossierEditor = (props: ArticleEditorProps) => {
	const { slug } = useParams<{ slug: string }>();
	const [id, setId] = useState<number>(-1);
	const [surtitle, setSurtitle] = useState<string>("");
	const [title, setTitle] = useState<string>("");
	const [urltitle, setUrltitle] = useState<string>("");
	const [metatitle, setMetatitle] = useState<string>("");
	const [long_hat, setLong_hat] = useState<string>("");
	const [short_hat, setShort_hat] = useState<string>("");
	const [description, setDescription] = useState<string>("");
	const [legend, setLegend] = useState<string>("");
	const [see_in_most_view, setSee_in_most_view] = useState<boolean>(true);
	const [theme, setTheme] = useState<any>(null);
	const [themes, setThemes] = useState<any[]>([]);
	const [mot_cles, setMot_cles] = useState<any[]>([]);
	const [readIntern, setReadIntern] = useState<any[]>([]);
	const [image, setImage] = useState<any[]>([]);
	const [send_to_partner_feed, setSend_to_partner_feed] = useState<boolean>(true);
	const [message, setMessage] = useState<string>("");
	const [publish_date, setPublish_date] = useState<null | Date>(null);
	const [share, setShare] = useState<Share>(Share.Share);
	const [notif, setNotif] = useState<Notif>(Notif.Notif);
	const [comment, setComment] = useState<string>("");

	const update = () => {
		const article = {
			id,
			surtitle,
			title,
			urltitle,
			long_hat,
			short_hat,
			description,
			legend,
			metatitle,
			see_in_most_view,
			theme,
			themes,
			mot_cles,
			image: image?.[0] ?? null,
			...article_to_readintern(readIntern),
			send_to_partner_feed,
			message,
			share: Share[share],
			notif: Notif[notif],
		};
		const url = props.type === "edit" ? "dossiers/" + article.id + "/revision" : "dossiers";
		return Strapi.post(url, {}, article);
	}

	const get_article = () => {
		if (props.type === "edit" && slug) {
			Strapi.get(`/dossiers/${slug}`).then((r) => {
				setId(r.data.id);
				setSurtitle(r.data.surtitle ?? "");
				setTitle(r.data.title ?? "");
				setUrltitle(r.data.urltitle ?? "");
				setLong_hat(r.data.long_hat ?? "");
				setShort_hat(r.data.short_hat ?? "");
				setDescription(r.data.description ?? "");
				setLegend(r.data.legend ?? "");
				setMetatitle(r.data.metatitle ?? "");
				setSee_in_most_view(r.data.see_in_most_view ?? true);
				setTheme(r.data.theme ?? null);
				setThemes(r.data.themes ?? "");
				setMot_cles(r.data.mot_cles ?? "");
				setReadIntern(readintern_to_article(r.data.articles));
				setImage(r.data.image ? [r.data.image] : []);
				setSend_to_partner_feed(r.data.send_to_partner_feed);
				setPublish_date(r.data.publish_date ? new Date(r.data.publish_date) : null);
				setShare(Share[r.data.share as keyof typeof Share] ?? Share.Share)
				setNotif(Notif[r.data.notif as keyof typeof Notif] ?? Notif.NoNotif)
			});
		}
	}
	useEffect(get_article, [props.type, slug]);

	const isValid = useMemo(() => {
		if (!title) {
			setComment('Champ "Titre" manquant');
			return false;
		}
		if (!short_hat) {
			setComment('Champ "Chapeau court" manquant');
			return false;
		}
		if (!legend) {
			setComment('Champ "Legend" manquant');
			return false;
		}
		if (!theme) {
			setComment('Champ "Theme" manquant');
			return false;
		}
		if (mot_cles.length < 3) {
			setComment('Champ "Mot-clés" doit avoir 3 valeurs');
			return false;
		}
		setComment('');
		return true;
	}, [title, short_hat, legend, theme, mot_cles]);

	return (
		<div>
			<Grid container spacing={3} style={{flexGrow: 1, marginBottom: 0}}>
				<ArticleSnackbar publish_date={publish_date} setPublish_date={setPublish_date} disabled={!isValid} edtion_type={props.type} update={update} comment={comment} type="dossier"/>
				<Grid item xs={12}>
					<Title value={title} onChange={setTitle}/>
				</Grid>

				<Grid item xs={4}>
					<Surtitle value={surtitle} onChange={setSurtitle}/>
				</Grid>
				<Grid item xs={4}>
					<MetaTitle value={metatitle} onChange={setMetatitle}/>
				</Grid>
				<Grid item xs={4}>
					<UrlTitle  type={props.type} value={urltitle} onChange={setUrltitle}/>
				</Grid>
				<Grid item xs={6}>
					<ShortHat value={short_hat} onChange={setShort_hat}/>
				</Grid>
				<Grid item xs={6}>
					<LongHat value={long_hat} onChange={setLong_hat}/>
				</Grid>

				<ArticleField label="Description">
					<TinyMCE
						value={description}
						onEditorChange={setDescription}
					/>
				</ArticleField>
				<ArticleField label="Articles">
					<ArticlePicker value={readIntern} hideDossier={true} onChange={(v) => {
						setReadIntern(v);
					}} />
				</ArticleField>
				<Image value={image} onChange={setImage}/>
				<Legend value={legend} onChange={setLegend}/>
				<Grid item xs={6}>
					<MainTheme value={theme} onChange={setTheme}/>
				</Grid>
				<Grid item xs={6}>
					<SecondaryThemes value={themes} onChange={setThemes}/>
				</Grid>
				<Grid item xs={12} lg={8}>
					<Keywords value={mot_cles} onChange={setMot_cles} />
				</Grid>
				<Grid item xs={4} lg={2}>
					<SeeInMostView value={see_in_most_view} onChange={setSee_in_most_view}/>
				</Grid>
				<Grid item xs={4} lg={2}>
					<SendToPartnerFeed value={send_to_partner_feed} onChange={setSend_to_partner_feed}/>
				</Grid>
				<Grid item xs={6} lg={3}>
					<SharePicker value={share} onChange={setShare}/>
				</Grid>
				<Grid item xs={6} lg={3}>
					<NotificationPicker value={notif} articleType="dossier" article={{title: title, slug: slug, short_hat: short_hat}} onChange={setNotif}/>
				</Grid>
				<RevisionMessage value={message} onChange={setMessage}/>
			</Grid>
		</div>
	);
}

export default DossierEditor;